import { db } from '../lib/firebase';
import { collection, doc, getDoc, setDoc, onSnapshot, query, where, getDocs } from 'firebase/firestore';
import type { Trade } from '../types';
import { getAuth } from 'firebase/auth';
import { applyBasicTradeWindow, resolveIsBasicLimitedTier } from '../lib/basicTierLimits';
import { isBreakEven, getTradeNetPnL } from '../utils/journal/tradeUtils';

export interface AccountStats {
  totalTrades: number;
  winningTradesCount: number;
  losingTradesCount: number;
  breakEvenTradesCount: number;
  winRate: number;
  totalProfit: number;
  totalReturnPercentage: number;
  currentBalance: number;
  maxBalance: number;
  profitFactor: {
    profits: number;
    losses: number;
  };
  averageRiskReward: number;
  maxDrawdown: number;
  averageTradeReturn: number;
  lastUpdated: Date;
  dataQuality: {
    score: number;
    status: 'complete' | 'partial' | 'incomplete';
    completedFieldsPercentage: number;
  };
}

class AccountStatsService {
  private getCashFlowType(cashFlow: any): string {
    return typeof cashFlow?.type === 'string' ? cashFlow.type.toUpperCase() : '';
  }

  private isDepositCashFlow(cashFlow: any): boolean {
    const type = this.getCashFlowType(cashFlow);
    return type === 'DEPOSIT' || type.includes('CREDIT');
  }

  private isWithdrawalCashFlow(cashFlow: any): boolean {
    const type = this.getCashFlowType(cashFlow);
    return type.includes('WITHDRAW') || type.includes('DEBIT');
  }

  private getTradeCompleteness(trade: Trade): { status: 'complete' | 'partial' | 'incomplete'; percentage: number } {
    const criteria = [
      { name: 'profitLoss', check: () => trade.profitLoss !== undefined && trade.profitLoss !== null },
      { name: 'entryPrice', check: () => trade.entryPrice !== undefined && trade.entryPrice !== null },
      { name: 'takeProfit', check: () => {
        const tp = trade.platform === 'ctrader' && trade.order_details?.order 
          ? trade.order_details.order.takeProfit 
          : trade.takeProfit;
        return tp !== undefined && tp !== null;
      }},
      { name: 'stopLoss', check: () => {
        const sl = trade.platform === 'ctrader' && trade.order_details?.order 
          ? trade.order_details.order.stopLoss 
          : trade.stopLoss;
        return sl !== undefined && sl !== null;
      }},
      { name: 'timeframe', check: () => trade.timeframe !== undefined && trade.timeframe !== null && trade.timeframe !== '' },
      { name: 'lotSize', check: () => {
        return (trade.lotSize !== undefined && trade.lotSize !== null);
      }},
      { name: 'riskPercentage', check: () => trade.riskPercentage !== undefined && trade.riskPercentage !== null },
      { name: 'riskRewardRatio', check: () => trade.riskRewardRatio !== undefined && trade.riskRewardRatio !== null },
      { name: 'mood', check: () => trade.mood !== undefined && trade.mood !== null },
      { name: 'setup', check: () => trade.setups !== undefined && trade.setups !== null && trade.setups.length > 0 }
    ];

    const completedCriteria = criteria.filter(criterion => criterion.check());
    const percentage = (completedCriteria.length / criteria.length) * 100;

    if (percentage === 100) {
      return { status: 'complete', percentage };
    } else if (percentage >= 70) {
      return { status: 'partial', percentage };
    } else {
      return { status: 'incomplete', percentage };
    }
  }

  private calculateDataQuality(trades: Trade[]): { score: number; status: 'complete' | 'partial' | 'incomplete'; completedFieldsPercentage: number } {
    if (trades.length === 0) {
      return { score: 0, status: 'incomplete', completedFieldsPercentage: 0 };
    }

    const completenessScores = trades.map(trade => this.getTradeCompleteness(trade));
    const totalPercentage = completenessScores.reduce((sum, score) => sum + score.percentage, 0);
    const averagePercentage = totalPercentage / trades.length;

    let status: 'complete' | 'partial' | 'incomplete';
    if (averagePercentage === 100) {
      status = 'complete';
    } else if (averagePercentage >= 70) {
      status = 'partial';
    } else {
      status = 'incomplete';
    }

    return {
      score: Math.round(averagePercentage * 100) / 100,
      status,
      completedFieldsPercentage: Math.round(averagePercentage * 100) / 100
    };
  }

  private normalizeDate(dateValue: any): Date {
    if (!dateValue) return new Date();
    
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    if (typeof dateValue === 'object' && dateValue !== null && 'seconds' in dateValue) {
      return new Date(dateValue.seconds * 1000);
    }
    
    if (typeof dateValue === 'object' && dateValue !== null && 'toDate' in dateValue) {
      return dateValue.toDate();
    }
    
    return new Date(dateValue);
  }

  private calculateStats(trades: Trade[], initialBalance: number, breakEvenThreshold?: number, cashFlows: any[] = [], preferences?: any): AccountStats {
    // Filtrer les trades fermés ET inclus dans les statistiques
    const closedTrades = trades.filter(trade => 
      trade.exitPrice !== undefined && 
      trade.exitPrice !== null &&
      (trade as any).includedInStats !== false // Par défaut inclus si le champ n'existe pas
    );
    
    // Utiliser la logique isBreakEven avec les préférences utilisateur
    const winningTrades: Trade[] = [];
    const losingTrades: Trade[] = [];
    const breakEvenTrades: Trade[] = [];

    closedTrades.forEach(trade => {
      if (!trade.profitLoss && trade.profitLoss !== 0) return;

      const accountContext = { initialBalance, currentBalance: initialBalance };

      if (trade.outcome === 'BREAK_EVEN' || isBreakEven(trade, preferences, accountContext)) {
        breakEvenTrades.push(trade);
      } else if (getTradeNetPnL(trade) > 0) {
        winningTrades.push(trade);
      } else if (getTradeNetPnL(trade) < 0) {
        losingTrades.push(trade);
      } else {
        // P/L brut à 0 : toujours considéré comme break even
        breakEvenTrades.push(trade);
      }
    });

    const profitFactor = this.calculateProfitFactor(winningTrades, losingTrades);
    const totalProfit = closedTrades.reduce((sum, trade) => sum + getTradeNetPnL(trade), 0);

    // Filtrer les cash flows pour ignorer seulement le premier dépôt AUTOMATIQUE (pas manuel)
    let filteredCashFlows = [...cashFlows];
    
    // Séparer les dépôts automatiques (avec platform/operationId) des dépôts manuels
    const automaticDeposits = cashFlows
      .filter(cf => this.getCashFlowType(cf) === 'DEPOSIT' && (cf.platform || cf.operationId))
      .sort((a, b) => {
        const dateA = this.normalizeDate(a.timestamp) || this.normalizeDate(a.createdAt) || this.normalizeDate(a.date) || new Date();
        const dateB = this.normalizeDate(b.timestamp) || this.normalizeDate(b.createdAt) || this.normalizeDate(b.date) || new Date();
        return dateA.getTime() - dateB.getTime();
      });
    
    const manualDeposits = cashFlows
      .filter(cf => this.getCashFlowType(cf) === 'DEPOSIT' && !cf.platform && !cf.operationId);
    
    // console.log supprimé
    // console.log supprimé
    
    // Ignorer seulement le premier dépôt automatique (celui qui correspond au solde initial)
    if (automaticDeposits.length > 0) {
      const firstAutomaticDepositId = automaticDeposits[0].id;
      filteredCashFlows = cashFlows.filter(cf => cf.id !== firstAutomaticDepositId);
      // console.log supprimé
    } else {
      // console.log supprimé
    }
    
    // Ensuite, filtrer par includedInStats (ne garder que ceux inclus dans les stats)
    filteredCashFlows = filteredCashFlows.filter(cf => (cf as any).includedInStats !== false);
    
    // Calculer les totaux pour debug
    const totalDeposits = cashFlows
      .filter(cf => this.isDepositCashFlow(cf))
      .reduce((sum, cf) => sum + Math.abs(cf.amount), 0);
    
    const totalWithdrawals = cashFlows
      .filter(cf => this.isWithdrawalCashFlow(cf))
      .reduce((sum, cf) => sum + Math.abs(cf.amount), 0);
    
    const totalFilteredDeposits = filteredCashFlows
      .filter(cf => this.isDepositCashFlow(cf))
      .reduce((sum, cf) => sum + Math.abs(cf.amount), 0);
    
    const totalFilteredWithdrawals = filteredCashFlows
      .filter(cf => this.isWithdrawalCashFlow(cf))
      .reduce((sum, cf) => sum + Math.abs(cf.amount), 0);
    
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé

    // Trier les trades et les cashflows filtrés par date pour un calcul chronologique précis
    const allTransactions = [
      ...closedTrades.map(trade => {
        // Normaliser la date du trade
        let tradeDate = new Date();
        if (trade.closeDateTime) {
          tradeDate = this.normalizeDate(trade.closeDateTime);
        } else if (trade.openDateTime) {
          tradeDate = this.normalizeDate(trade.openDateTime);
        }
        
        return {
          date: tradeDate,
          amount: getTradeNetPnL(trade),
          type: 'trade'
        };
      }),
      ...filteredCashFlows.map(cf => {
        // Déterminer si le montant doit être positif ou négatif en fonction du type
        let adjustedAmount = cf.amount;
        
        // Si le montant est déjà négatif, ne pas l'inverser à nouveau pour les retraits
        if (this.isWithdrawalCashFlow(cf)) {
          adjustedAmount = cf.amount < 0 ? cf.amount : -cf.amount;
        } else if (this.isDepositCashFlow(cf)) {
          adjustedAmount = cf.amount < 0 ? -cf.amount : cf.amount;
        }
        
        return {
          date: this.normalizeDate(cf.timestamp) || this.normalizeDate(cf.createdAt) || this.normalizeDate(cf.date) || new Date(),
          amount: adjustedAmount,
          type: 'cashflow'
        };
      })
    ].sort((a, b) => a.date.getTime() - b.date.getTime());

    let runningBalance = initialBalance;
    let maxBalance = initialBalance;
    
    // Calculer la balance en tenant compte des trades et des cash flows par ordre chronologique
    allTransactions.forEach(transaction => {
      runningBalance += transaction.amount;
      maxBalance = Math.max(maxBalance, runningBalance);
    });

    const dataQuality = this.calculateDataQuality(trades);

    // 🎯 CORRECTION: Calculer le montant total investi (initial + dépôts nets)
    const netCashFlowsAmount = totalFilteredDeposits - totalFilteredWithdrawals;
    const totalInvested = initialBalance + netCashFlowsAmount;
    const correctedReturnPercentage = totalInvested > 0 ? (totalProfit / totalInvested) * 100 : 0;

    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé

    const finalStats = {
      totalTrades: trades.length,
      winningTradesCount: winningTrades.length,
      losingTradesCount: losingTrades.length,
      breakEvenTradesCount: breakEvenTrades.length,
      winRate: (winningTrades.length + losingTrades.length) > 0 ? (winningTrades.length / (winningTrades.length + losingTrades.length)) * 100 : 0,
      totalProfit,
      totalReturnPercentage: correctedReturnPercentage,
      currentBalance: runningBalance,
      maxBalance,
      profitFactor,
      averageRiskReward: this.calculateAverageRiskReward(closedTrades),
      maxDrawdown: this.calculateMaxDrawdown(trades, initialBalance),
      averageTradeReturn: this.calculateAverageTradeReturn(closedTrades),
      lastUpdated: new Date(),
      dataQuality
    };

    // console.log supprimé
    // console.log supprimé  
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé
    // console.log supprimé

    return finalStats;
  }

  private calculateProfitFactor(winningTrades: Trade[], losingTrades: Trade[]) {
    const totalProfits = winningTrades.reduce((sum, trade) => sum + getTradeNetPnL(trade), 0);

    const totalLosses = Math.abs(losingTrades.reduce((sum, trade) => sum + getTradeNetPnL(trade), 0));
    
    return {
      profits: totalProfits,
      losses: totalLosses
    };
  }

  private calculateAverageRiskReward(trades: Trade[]) {
    const tradesWithRR = trades.filter(trade => trade.riskRewardRatio !== undefined && trade.riskRewardRatio !== null);
    if (tradesWithRR.length === 0) return 0;
    
    const validTrades = tradesWithRR.filter(trade => {
      if (!trade.riskRewardRatio) return false;

      // Si c'est déjà un nombre
      if (typeof trade.riskRewardRatio === 'number') {
        return true;
      }

      // Si c'est une chaîne au format "1 : X"
      if (typeof trade.riskRewardRatio === 'string') {
        try {
          if (!trade.riskRewardRatio.includes(':')) {
            const value = parseFloat(trade.riskRewardRatio);
            return !isNaN(value);
          }

          const [risk, reward] = trade.riskRewardRatio
            .split(':')
            .map(part => parseFloat(part.replace(/[^\d.-]/g, '')));

          return !isNaN(risk) && !isNaN(reward) && risk === 1;
        } catch (error) {
          return false;
        }
      }

      return false;
    });

    if (validTrades.length === 0) return 0;

    const totalReward = validTrades.reduce((sum, trade) => {
      const rr = trade.riskRewardRatio;
      
      // Si c'est déjà un nombre
      if (typeof rr === 'number') {
        return sum + rr;
      }

      // Si c'est une chaîne
      if (typeof rr === 'string') {
        if (!rr.includes(':')) {
          return sum + parseFloat(rr);
        }

        const [, reward] = rr
          .split(':')
          .map(part => parseFloat(part.replace(/[^\d.-]/g, '')));
        return sum + reward;
      }

      return sum;
    }, 0);

    return totalReward / validTrades.length;
  }

  private calculateMaxDrawdown(trades: Trade[], initialBalance: number): number {
    let balance = initialBalance;
    let maxBalance = initialBalance;
    let maxDrawdown = 0;

    trades.forEach(trade => {
      if (trade.exitPrice !== undefined && trade.exitPrice !== null) {
        const netPL = getTradeNetPnL(trade);
        balance += netPL;
        maxBalance = Math.max(maxBalance, balance);
        const drawdown = ((maxBalance - balance) / maxBalance) * 100;
        maxDrawdown = Math.max(maxDrawdown, drawdown);
      }
    });

    return maxDrawdown;
  }

  private calculateAverageTradeReturn(trades: Trade[]): number {
    if (trades.length === 0) return 0;
    const totalReturn = trades.reduce((sum, trade) => sum + getTradeNetPnL(trade), 0);
    return totalReturn / trades.length;
  }

  private async getUserPreferences(userId: string) {
    const userPrefsDoc = await getDoc(doc(db, 'userPreferences', userId));
    return userPrefsDoc.exists() ? userPrefsDoc.data() : null;
  }

  /**
   * Met à jour les statistiques d'un compte
   * @param accountId ID du compte
   * @param trades Liste des trades
   * @param initialBalance Solde initial du compte
   * @param breakEvenThreshold Seuil de break-even (optionnel)
   * @param cashFlows Liste des mouvements de fonds (optionnel)
   * @returns Les statistiques mises à jour
   */
  async updateAccountStats(accountId: string, trades: Trade[], initialBalance: number, breakEvenThreshold?: number, cashFlows: any[] = []) {
    try {
      // console.log supprimé
      // console.log supprimé
      // console.log supprimé
      // console.log supprimé
      // console.log supprimé
      // console.log supprimé
      
      const auth = getAuth();
      if (!auth.currentUser) throw new Error("User not authenticated");

      const preferences = await this.getUserPreferences(auth.currentUser.uid);
      
      // Récupérer les cash flows si non fournis en paramètre
      let cashFlowsData = cashFlows;
      if (cashFlowsData.length === 0) {
        // console.log supprimé
        try {
          const cashFlowsRef = collection(db, 'cashFlows');
          const q = query(cashFlowsRef, where('accountId', '==', accountId));
          const querySnapshot = await getDocs(q);
          cashFlowsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          // console.log supprimé
        } catch (error) {
          // console.log supprimé
          // Ignorer les erreurs, continuer avec un tableau vide
          cashFlowsData = [];
        }
      } else {
        // console.log supprimé
      }

      const isLimitedTier = await resolveIsBasicLimitedTier(auth.currentUser.uid);
      const { visibleTrades } = applyBasicTradeWindow(trades, isLimitedTier);
      const stats = this.calculateStats(
        visibleTrades,
        initialBalance,
        breakEvenThreshold,
        cashFlowsData,
        preferences,
      );

      // Mise à jour des statistiques dans Firestore
      await setDoc(doc(db, 'accountStats', accountId), stats);
      
      return stats;
    } catch (error) {
      throw error;
    }
  }

  // Cache pour les statistiques de compte
  private statsCache: { [accountId: string]: { stats: AccountStats | null, timestamp: number } } = {};
  
  /**
   * Récupère les statistiques d'un compte
   * @param accountId ID du compte
   * @param forceRefresh Force le rafraîchissement des données (ignore le cache)
   * @returns Les statistiques du compte ou null si non trouvées
   */
  async getAccountStats(accountId: string, forceRefresh: boolean = false): Promise<AccountStats | null> {
    // Vérifier si les stats sont en cache et si elles sont récentes (moins de 5 secondes)
    const cachedStats = this.statsCache[accountId];
    const now = Date.now();
    
    if (!forceRefresh && cachedStats && (now - cachedStats.timestamp) < 5000) {
      return cachedStats.stats;
    }
    
    try {
      const statsRef = doc(db, 'accountStats', accountId);
      const statsDoc = await getDoc(statsRef);
      const stats = statsDoc.exists() ? statsDoc.data() as AccountStats : null;
      
      // Mettre à jour le cache
      this.statsCache[accountId] = { stats, timestamp: now };
      
      return stats;
    } catch (error) {
      // console.error supprimé
      // En cas d'erreur, retourner les données en cache si disponibles
      return cachedStats?.stats || null;
    }
  }

  /**
   * S'abonne aux changements des statistiques d'un compte
   * @param accountId ID du compte
   * @param callback Fonction appelée à chaque mise à jour des statistiques
   * @returns Fonction pour se désabonner
   */
  subscribeToAccountStats(accountId: string, callback: (stats: AccountStats | null) => void) {
    const statsRef = doc(db, 'accountStats', accountId);
    
    // Utiliser onSnapshot pour recevoir les mises à jour des statistiques
    return onSnapshot(statsRef, (doc) => {
      if (doc.exists()) {
        const stats = doc.data() as AccountStats;
        
        // Mettre à jour le cache
        this.statsCache[accountId] = { stats, timestamp: Date.now() };
        
        // Appeler le callback avec les nouvelles données
        callback(stats);
      } else {
        // Mettre à jour le cache pour indiquer qu'il n'y a pas de stats
        this.statsCache[accountId] = { stats: null, timestamp: Date.now() };
        callback(null);
      }
    }, (error) => {
      // console.error supprimé
      // Ne pas appeler le callback en cas d'erreur pour éviter de perturber l'UI
    });
  }

  // Méthode publique pour calculer les stats à partir d'une liste de trades filtrés
  public calculateStatsFromTrades(trades: Trade[], initialBalance: number = 0, preferences?: any, cashFlows: any[] = []): AccountStats {
    return this.calculateStats(trades, initialBalance, undefined, cashFlows, preferences);
  }
}

export const accountStatsService = new AccountStatsService();
